import { Component, inject, OnInit } from '@angular/core';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { DialPlanExceptionResource } from '../../resources/dial-plan-exception.resource';
import { TranslateService } from '@ngx-translate/core';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../shared/bottom-nav/bottom-nav.service';
import { ToastService } from '../../../shared/services/toast.service';
import { DialPlanExceptionGroup, DnDidRange } from '../../../shared/models/generated/ziroModels';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { Observable } from 'rxjs';
import { DialPlanExceptionManagementEditComponent } from './dial-plan-exception-management-edit/dial-plan-exception-management-edit.component';
import { RangeFilterService } from '../../services/range-filter.service';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { ZiroModalPromptModalOptions } from '../../../modals/prompt-modal/prompt-modal.component';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { DatatableBadge, DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';

interface UiDialPlanExceptionGroup extends DialPlanExceptionGroup {
  exceptions?: DnDidRange[];
}

interface DialPlanExceptionManagementRow extends DatatableRow {
  groupName: string;
  exceptions: DnDidRange[];
}

@Component({
  selector: 'ziro-admin-exception-management',
  templateUrl: './dial-plan-exception-management.component.html',
  styleUrls: ['../../admin-page.scss'],
  providers: [RangeFilterService],
  standalone: false,
})
export class DialPlanExceptionManagementComponent implements OnInit {
  private _ziroModalService = inject(ZiroModalService);
  private _ziroFormsStateService = inject(ZiroFormStateService);
  private _toastService = inject(ToastService);
  private _dialPlanExceptionResource = inject(DialPlanExceptionResource);
  private _translateService = inject(TranslateService);
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _rangeFilterService = inject(RangeFilterService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);

  isLoading = true;
  columns: DatatableColumn<DialPlanExceptionManagementRow>[] = [
    {
      name: 'groupName',
      label: 'tkey;dialplanmanagement.admin.group.name',
      columnSize: 'col-sm-4',
    },
    {
      name: 'exceptions',
      label: 'tkey;dnexception.admin.group.range.exceptions',
      columnSize: 'col-sm-7',
      sortFn: (a: DnDidRange[], b: DnDidRange[]) => {
        const aRange: DnDidRange = a[0] || { start: '0', end: '' };
        const bRange: DnDidRange = b[0] || { start: '0', end: '' };
        return parseInt(aRange.start) - parseInt(bRange.start);
      },
    },
  ];
  data: DialPlanExceptionManagementRow[] = [];
  tableDataAutomation = 'dial-plan-exception-management-table';

  private _dnExceptionGroups: UiDialPlanExceptionGroup[] = [];

  ngOnInit(): void {
    this._loadDnExceptionGroups().subscribe();
    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;dnexception.admin.title' }]);
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'addGroupButton',
          label: 'tkey;dialplanmanagement.admin.add_group',
          icon: ZiroIcons.ADD,
          buttonClass: ButtonStyles.PRIMARY,
          dataAutomation: 'bottom-nav-add-group-button',
          cb: () => {
            this._showEdit({
              exceptionRanges: [],
              exceptionValues: [],
              legalHold: false,
              name: '',
            } as DialPlanExceptionGroup);
          },
          state: {
            pending: false,
            buttonDisableState: { disabled: false, tooltipKey: '' },
          },
        },
      ]),
    );
  }

  private _onEditClicked(dnExceptionGroup: DialPlanExceptionGroup): void {
    this._showEdit(dnExceptionGroup, true);
  }

  private _onDeleteClicked(dnExceptionGroup: DialPlanExceptionGroup): void {
    const options = {
      modalViewProperties: {
        promptBody: this._translateService.instant('tkey;global.sure.delete.dnexception.group', {
          name: dnExceptionGroup.name,
        }),
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._onConfirmDeleteModalYes(dnExceptionGroup),
          },
        ],
      },
    };
    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _groupNameComparator: (g1: DialPlanExceptionGroup, g2: DialPlanExceptionGroup) => number = (
    g1: DialPlanExceptionGroup,
    g2: DialPlanExceptionGroup,
  ) => g1.name.localeCompare(g2.name);

  private _getExceptionValues = (dnExceptionGroup: DialPlanExceptionGroup): DnDidRange[] => {
    const exceptionRanges = dnExceptionGroup.exceptionRanges || [];
    const exceptionValues: DnDidRange[] = (dnExceptionGroup.exceptionValues || []).map((value) => {
      return {
        start: value,
        end: value,
      };
    });

    return exceptionRanges.concat(exceptionValues);
  };

  private _loadDnExceptionGroups(): Observable<any> {
    return new Observable<void>((subscriber) => {
      this._dialPlanExceptionResource.getAll().subscribe((groups) => {
        this._dnExceptionGroups = groups.sort(this._groupNameComparator).map((group: DialPlanExceptionGroup) => {
          return {
            ...group,
            exceptions: this._getExceptionValues(group),
          };
        });
        this._generateTable();
        this.isLoading = false;
        subscriber.next();
      });
    });
  }

  private _generateTable(): void {
    this.data = this._dnExceptionGroups.map((group: UiDialPlanExceptionGroup): DialPlanExceptionManagementRow => {
      return {
        groupName: group.name,
        exceptions: group.exceptions,
        badges: {
          exceptions: {
            values: group.exceptions.map(
              (range: DnDidRange): DatatableBadge<DialPlanExceptionManagementRow> =>
                range.start === range.end
                  ? { label: this._telephoneNumberFilter.transform(range.start), cssClass: 'bg-secondary' }
                  : { label: this._rangeFilterService.getE164FormattedRange(range), cssClass: 'bg-secondary' },
            ),
          },
        },
        actions: [
          {
            dataAutomation: 'edit-exception-group-button',
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_PRIMARY,
            icon: ZiroIcons.EDIT,
            onClick: () => this._onEditClicked(group),
          },
          {
            dataAutomation: 'delete-exception-group-button',
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_DANGER,
            icon: ZiroIcons.DELETE_SMALL,
            onClick: () => this._onDeleteClicked(group),
          },
        ],
      };
    });
  }

  private _showEdit(dnExceptionGroup: DialPlanExceptionGroup, isEdit?: boolean): void {
    const options = {
      modalViewProperties: {
        title: isEdit ? 'tkey;dnexception.admin.group.edit' : 'tkey;dnexception.admin.group.add',
        dnExceptionGroup: { ...dnExceptionGroup },
        allGroups: this._dnExceptionGroups.filter(
          (group: UiDialPlanExceptionGroup) => group.id !== dnExceptionGroup.id,
        ),
      },
      bodyClass: DialPlanExceptionManagementEditComponent,
      size: 'lg',
    } as ZiroModalPromptModalOptions;
    this._ziroModalService
      .openDetailedModal(() => options.modalViewProperties, options)
      .subscribe((result) => {
        if (result) {
          this._loadDnExceptionGroups().subscribe(() => {
            this._ziroFormsStateService.setIsFormDirty(false);
            this._toastService.pushSaveToast('tkey;dnexceptiongroup.name', result.name, ZiroIcons.SAVE_SUCCESS);
          });
        }
      });
  }

  private _onConfirmDeleteModalYes(dnExceptionGroup: DialPlanExceptionGroup): Observable<any> {
    return new Observable((subscriber) => {
      this._dialPlanExceptionResource.delete(dnExceptionGroup.id).subscribe(() => {
        this._loadDnExceptionGroups().subscribe(() => {
          this._toastService.pushDeleteToast('tkey;dnexceptiongroup.name', dnExceptionGroup.name);
          subscriber.next(true);
        });
      });
    });
  }
}
